import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {formatDateTime} from "../../utils/helpers";
import {Router} from "../../router";
import {
    BetsService,
    DataService,
    MatchesService,
    MatchWithBetSchema,
    SeasonSchema,
    TeamSchema,
    UserScoreSchema
} from "../../api";
import {useAuth} from "../../contexts/AuthContext";
import {Skeleton} from "../../components/Skeleton";
import {BetResultTooltip} from "../../components/BetResultTooltipProps/BetResultTooltipProps";
import Header from "../../components/Header";
import {Footer} from "../../components/Footer";

export const Home: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<SeasonSchema>({name: ''});
    const [nextMatch, setNextMatch] = useState<MatchWithBetSchema>();
    const [userScores, setUserScoreSchemas] = useState<UserScoreSchema[]>([]);
    const {user} = useAuth();
    const [loading, setLoading] = useState<boolean>(true);
    const [recentMatches, setRecentMatches] = useState<MatchWithBetSchema[]>([]);
    const [qualifiedTeams, setQualifiedTeams] = useState<Set<string>>(new Set());


    const STAGE_TITLES: Record<string, string> = {
        'Групповой этап': 'Выход из группы',
        '1/2 финала': 'Полуфинал',
    };

    const [tournamentStats, setTournamentStats] = useState<{
        currentRound: string;
        guessed: string[];
        missed: string[];
        notSelected: string[];
    } | null>(null);
    const [allTeams, setAllTeams] = useState<Record<string, TeamSchema>>({});

    useEffect(() => {
        Promise.all([
            DataService.getSeason(),
            MatchesService.getMatchNext(),
            MatchesService.recentMatches(),
            BetsService.getLeaderboard(),
            BetsService.tournamentResults(),
            BetsService.tournamentPredictions(),
            BetsService.tournamentData(),  // <-- добавить
        ]).then(([seasonData, matchNextData, recentData, leaderboardData, results, predictions, tournamentInfo]) => {
            setData(seasonData);
            setNextMatch(matchNextData);
            setRecentMatches(recentData);
            setUserScoreSchemas(leaderboardData);

            // Команды
            const teamsMap: Record<string, TeamSchema> = {};
            for (const t of (tournamentInfo as any).teams) {
                teamsMap[t.id] = t;
            }
            setAllTeams(teamsMap);

            // Квалифицированные
            const qualified = new Set<string>();
            const allValues = Object.values(results) as string[][];
            if (allValues.length > 0) {
                const latestStage = allValues[allValues.length - 1];
                for (const teamId of latestStage) {
                    qualified.add(teamId);
                }
            }
            setQualifiedTeams(qualified);

            // Статистика турнира для текущего раунда
            const allStages = Object.keys(results);
            if (allStages.length > 0) {
                const currentRound = [...Object.keys(results)].reverse().find(stage => (results[stage] as string[]).length > 0) || '';
                if (!currentRound) return;
                const realTeams = results[currentRound] as string[];
                const pred = (predictions as any[]).find((p: any) => p.round_name === currentRound);
                const userSelected: string[] = pred?.selected_team_ids || [];

                const guessed = userSelected.filter((id: string) => realTeams.includes(id));
                const missed = userSelected.filter((id: string) => !realTeams.includes(id));
                const notSelected = realTeams.filter((id: string) => !userSelected.includes(id));

                setTournamentStats({ currentRound, guessed, missed, notSelected });
            }
        }).finally(() => setLoading(false));
    }, []);
    if (loading) {
        return <Skeleton/>;
    }

    return (
        <>
            <Header fixed={true}/>
            <main className='content'>
                <section className='hero'>
                    <div className='hero__container'>
                        <h1 className="text-white">{data.name}</h1>
                        {nextMatch && (
                            <div className='hero__match-card'>
                                <div className='hero__match-header'>
                                    {nextMatch.status === 'LIVE' ? 'Прямой эфир' : 'Ближайшая игра'}
                                    <span
                                        className='hero__match-round'> • {nextMatch.round_name}{nextMatch.round?.group && `, ${nextMatch.round.group}`}</span>
                                </div>

                                <div className='hero__match-body'>
                                    <div className='hero__match-side'>
                                        <img className='flag__image' src={nextMatch.home?.team.logo}
                                             alt={nextMatch.home?.team.name}/>
                                        <span className='hero__match-team-name'>{nextMatch.home?.team.name}</span>
                                    </div>

                                    <div className='hero__match-center'>
                                        {nextMatch.status === 'LIVE' ? (
                                            <>
                                                <div className='match__score'>
                                                    <span>{nextMatch.home?.score}</span>
                                                    <span>-</span>
                                                    <span>{nextMatch.away?.score}</span>
                                                </div>
                                                {nextMatch.current_minute && (
                                                    <div
                                                        className='hero__match-minute'>{nextMatch.current_minute}'</div>
                                                )}
                                            </>
                                        ) : (
                                            <div className='hero__match-time'>
                                                {formatDateTime(nextMatch.scheduled_at)}
                                            </div>
                                        )}
                                        <a className='hero__match-link'
                                           href={Router.MATCH.replace(':matchId', nextMatch.id.toString())}>
                                            Детали матча →
                                        </a>
                                    </div>
                                    <div className='hero__match-side'>
                                        <img className='flag__image' src={nextMatch.away?.team.logo}
                                             alt={nextMatch.away?.team.name}/>
                                        <span className='hero__match-team-name'>{nextMatch.away?.team.name}</span>
                                    </div>
                                </div>

                                {nextMatch.bet && (
                                    <div className='hero__match-bet'>
                                        Твой прогноз: <strong>{nextMatch.bet.home} – {nextMatch.bet.away}</strong>
                                        {nextMatch.bet.overtime_home !== null && (
                                            <> (доп. {nextMatch.bet.overtime_home}–{nextMatch.bet.overtime_away}{nextMatch.bet.penalty_home !== null && `, пен. ${nextMatch.bet.penalty_home}–${nextMatch.bet.penalty_away}`})</>
                                        )}
                                        {nextMatch.bet.stake! > 0 && (
                                            <> • {nextMatch.bet.stake} баллов{nextMatch.bet.is_all_in ? ' (ва-банк)' : ''}{nextMatch.bet.is_golden ? ' ×2' : ''}</>
                                        )}
                                    </div>
                                )}

                                <button className='hero__match-btn' onClick={() => navigate(Router.SCHEDULE)}>
                                    Перейти к прогнозам
                                </button>
                            </div>
                        )}    </div>
                </section>
                {
                    recentMatches.length > 0 && (
                        <section className='rates'>
                            <h3 className='rates__title'>Последние матчи</h3>
                            <div className='rates__scroll'>
                                <table className='rates__table'>
                                    <thead>
                                    <tr>
                                        <th>Матч</th>
                                        <th className='rates__cell--center'>Счёт</th>
                                        <th className='rates__cell--center'>Прогноз</th>
                                        <th className='rates__cell--right'>Итог</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {recentMatches.map((match) => {
                                        const bet = match.bet;
                                        const totalResult = bet && match.status === 'CLOSED'
                                            ? (bet.classic_points ?? 0) + (bet.stake_profit ?? 0)
                                            : null;
                                        const isQualified = (teamId: string) => qualifiedTeams.has(teamId);

                                        return (
                                            <tr key={match.id}>
                                                <td className='rates__cell--match'>
                                                    <a href={`/matches/${match.id}`} className='rates__match-link'>
                                                        {match.home?.team.name} – {match.away?.team.name}
                                                    </a>
                                                    <div className='rates__cell--small'>{match.round_name}{match.round?.group && `, ${match.round.group}`}</div>
                                                </td>
                                                <td className='rates__cell--center rates__cell--score'>
                                                    {match.status === 'CLOSED' ? (
                                                        <>
                                                            {match.home?.overtime_score !== null && match.away?.overtime_score !== null ? (
                                                                <>
                                                                    <div>{match.home?.overtime_score}:{match.away?.overtime_score}</div>
                                                                    <div className='rates__cell--small'>осн. {match.home?.score}:{match.away?.score}</div>
                                                                    {match.home?.penalty_score !== null && (
                                                                        <div className='rates__cell--small'>пен. {match.home?.penalty_score}:{match.away?.penalty_score}</div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>{match.home?.score}:{match.away?.score}</>
                                                            )}
                                                            <div className='rates__qualify-list'>
                                                                {isQualified(match.home!.team.id) && (
                                                                    <span className='rates__qualify-badge'>выход</span>
                                                                )}
                                                                {isQualified(match.away!.team.id) && (
                                                                    <span className='rates__qualify-badge'>выход</span>
                                                                )}
                                                            </div>
                                                        </>
                                                    ) : '—'}
                                                </td>
                                                <td className='rates__cell--center'>
                                                    {bet ? (
                                                        <>
                                                            {bet.home}:{bet.away}
                                                            {bet.overtime_home !== null && ` (${bet.overtime_home}:${bet.overtime_away})`}
                                                            {bet.penalty_home !== null && ` пен. ${bet.penalty_home}:${bet.penalty_away}`}
                                                        </>
                                                    ) : '—'}
                                                </td>
                                                <td className={`rates__cell--right ${
                                                    totalResult !== null ? (totalResult >= 0 ? 'rates__cell--positive' : 'rates__cell--negative') : ''
                                                }`}>
                                                    {totalResult !== null ? (
                                                        <BetResultTooltip bet={bet!} totalResult={totalResult}>
                                                            {`${totalResult >= 0 ? '+' : ''}${totalResult}`}
                                                        </BetResultTooltip>
                                                    ) : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )
                }

                {
                    tournamentStats && tournamentStats.guessed.length + tournamentStats.notSelected.length > 0 &&
                    <section className='rates'>
                        <h3 className='rates__title'>Турнир</h3>
                        <div className='user-profile__stage'>
                            <h4 className='user-profile__stage-title'>
                                {STAGE_TITLES[tournamentStats.currentRound] || tournamentStats.currentRound}
                                <span className='user-profile__stage-guessed'>
                                    {tournamentStats.guessed.length}/{tournamentStats.guessed.length + tournamentStats.notSelected.length}
                                </span>
                            </h4>
                            <div className='user-profile__teams'>
                                {tournamentStats.guessed.map(teamId => {
                                    const team = allTeams[teamId];
                                    if (!team) return null;
                                    return (
                                        <span key={teamId} className='user-profile__team user-profile__team--correct'>
                                            {team.logo && <img src={team.logo} alt="" className='user-profile__team-logo' />}
                                            {team.name}
                                        </span>
                                    );
                                })}
                                {tournamentStats.notSelected.map(teamId => {
                                    const team = allTeams[teamId];
                                    if (!team) return null;
                                    return (
                                        <span key={teamId} className='user-profile__team user-profile__team--not-selected'>
                                            {team.logo && <img src={team.logo} alt="" className='user-profile__team-logo' />}
                                            {team.name}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                }
                {
                    userScores.length > 0 && (
                        <section className='rates'>
                            <h3 className='rates__title'>Общий зачёт</h3>
                            <div className='rates__scroll'>
                                <table className='rates__table rates__table--leaderboard'>
                                    <thead>
                                    <tr>
                                        <th className='rates__cell--center'>#</th>
                                        <th>Игрок</th>
                                        <th className='rates__cell--right'>Рейтинг</th>
                                        <th className='rates__cell--right'>Матчи</th>
                                        <th className='rates__cell--right'>Турниры</th>
                                        <th className='rates__cell--right'>Ставки</th>
                                        <th className='rates__cell--right'>Прогнозов</th>
                                        <th className='rates__cell--right'>Точность</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {userScores.map((row) => {
                                        const isMe = user && row.user.id === user.id;
                                        const topClass = row.position === 1 ? 'rates__row--gold' :
                                            row.position === 2 ? 'rates__row--silver' :
                                                row.position === 3 ? 'rates__row--bronze' : '';

                                        return (
                                            <tr key={row.user.id} className={`${topClass} ${isMe ? 'rates__row--me' : ''}`}>
                                                <td className='rates__cell--center rates__cell--rank'>{row.position}</td>
                                                <td>
                                                    <div className='rates__user'>
                                                        {row.user.avatar && (
                                                            <img src={row.user.avatar} alt={row.user.username}
                                                                 className='rates__user-avatar'/>
                                                        )}
                                                        <div>
                                                            <a href={`/user/${row.user.id}`}
                                                               className='rates__user-name-link'>
                                                                {row.user.username}
                                                            </a>
                                                            {row.user.about && (
                                                                <div className='rates__user-about'>{row.user.about}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className='rates__cell--right rates__cell--score'>{row.rating_score}</td>
                                                <td className='rates__cell--right rates__cell--secondary'>{row.classic_points}</td>
                                                <td className='rates__cell--right rates__cell--secondary'>{row.tournament_points}</td>
                                                <td className='rates__cell--right rates__cell--secondary'>
                                                    {row.stake_profit! >= 0 ? '+' : ''}{row.stake_profit}
                                                </td>
                                                <td className='rates__cell--right rates__cell--secondary'>{row.bet_count}</td>
                                                <td className='rates__cell--right rates__cell--secondary'>
                                                    {row.accuracy ? <>{row.accuracy}%</> : <>—</>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}
            </main>
            <Footer/>
        </>
    );
};